import React from "react";

import { Status } from "@src/components/status";
import {
  DUALIS_TECHNOLOGY,
  PROJECT_PHASE,
  REQUEST_STATUS,
} from "@src/constants";
import PhaseRenderer from "@src/features/staffing_requests/custom_renderer/phase_renderer/phase_renderer";
import DateRangeFilter from "@src/features/table_filtering/components/date_range_filter/date_range_filter";
import SelectionFilter from "@src/features/table_filtering/components/selection_filter/selection_filter";
import { mapToTreeData } from "@src/features/table_filtering/utils/filter_utils";
import "./filter_staffing_requests.less";
import {
  setCustomDateFilter,
  setCustomTableFilter,
} from "@src/services/requestOverviewSlice";
import { RootState, useAppDispatch, useAppSelector } from "@src/setupStore";
import { REQUEST_OVERVIEW_FILTER_TYPES } from "@src/types";
import { TreeNode } from "@src/types/antd_types";
import dayjs, { Dayjs } from "dayjs";

interface FilterStaffingRequestsProps {
  countries: TreeNode[];
  regions: TreeNode[];
  projectName: TreeNode[];
  requestedRole: TreeNode[];
  disabled: boolean;
}

const FilterStaffingRequests: React.FC<FilterStaffingRequestsProps> = ({
  countries,
  regions,
  projectName,
  requestedRole,
  disabled,
}) => {
  const dispatch = useAppDispatch();

  const {
    requestOverviewTableState: { customTableFilters, customDateFilters },
  } = useAppSelector((state: RootState) => state.requestOverviewSlice);

  /**
   * Updates the custom table filter by the given type.
   *
   * @param selectedElements        selected elements to be filtered (eg. countries, regions, technologies)
   * @param fieldTypeName           ield type name of filter
   */
  function updateCustomTableFilterByType(
    selectedElements: string[],
    fieldTypeName: string
  ): void {
    dispatch(
      setCustomTableFilter({
        ...customTableFilters,
        [fieldTypeName]: selectedElements,
      })
    );
  }

  /**
   * Handles the change of date filters by converting Dayjs objects to ISO strings
   * and dispatching the updated date filter values to the Redux store.
   *
   * @param values - Array of Dayjs objects representing the selected date filters.
   * @param field - field type name of filter
   */
  function onDateChangeByField(values: Dayjs[], field: string): void {
    const isoStringValues = values?.map((date) => date.toISOString());
    dispatch(
      setCustomDateFilter({
        ...customDateFilters,
        [field]: isoStringValues,
      })
    );
  }

  /**
   * Parses an array of ISO date strings into an array of Dayjs objects.
   *
   * @param {string[]} dateFilter - Array of ISO date strings to be parsed.
   * @returns {Dayjs[]} - Array of Dayjs objects parsed from the input strings.
   */
  function parseDateFilter(dateFilter: string[]): Dayjs[] {
    if (!dateFilter || dateFilter.length === 0) {
      return [];
    }
    return dateFilter.map((date) => dayjs(date));
  }

  return (
    <>
      <DateRangeFilter
        title="WL Start"
        dateFilter={parseDateFilter(
          customDateFilters.roleRequestStart as string[]
        )}
        disabledFrom={disabled}
        disabledTo={disabled}
        onRangePickerChange={(values: Dayjs[]) =>
          onDateChangeByField(
            values,
            REQUEST_OVERVIEW_FILTER_TYPES.REQUEST_START
          )
        }
      />
      <DateRangeFilter
        title="Request Sent"
        dateFilter={parseDateFilter(
          customDateFilters.requestSentDate as string[]
        )}
        disabledFrom={disabled}
        disabledTo={disabled}
        onRangePickerChange={(values: Dayjs[]) =>
          onDateChangeByField(
            values,
            REQUEST_OVERVIEW_FILTER_TYPES.REQUEST_SENT
          )
        }
      />
      <SelectionFilter
        title="Requested Role"
        treeData={requestedRole}
        disabled={disabled}
        defaultKeys={customTableFilters.requestedRoleName as string[]}
        onFilter={(values: string[]) => {
          updateCustomTableFilterByType(
            values,
            REQUEST_OVERVIEW_FILTER_TYPES.REQUESTED_ROLE
          );
        }}
      />
      <SelectionFilter
        title="Phase"
        isDataStatic={true}
        treeData={mapToTreeData(Object.values(PROJECT_PHASE))}
        customRenderer={(node: TreeNode) => (
          <PhaseRenderer status={node.title.toString()} />
        )}
        disabled={disabled}
        defaultKeys={customTableFilters.projectPhase as string[]}
        onFilter={(values: string[]) => {
          updateCustomTableFilterByType(
            values,
            REQUEST_OVERVIEW_FILTER_TYPES.PROJECT_PHASE
          );
        }}
      />
      <SelectionFilter
        title="Project Name"
        treeData={projectName}
        disabled={disabled}
        defaultKeys={customTableFilters.projectName as string[]}
        onFilter={(values: string[]) => {
          updateCustomTableFilterByType(
            values,
            REQUEST_OVERVIEW_FILTER_TYPES.PROJECT_NAME
          );
        }}
      />
      <SelectionFilter
        title="Country"
        treeData={countries}
        disabled={disabled}
        defaultKeys={customTableFilters.country}
        onFilter={(values: string[]) => {
          updateCustomTableFilterByType(
            values,
            REQUEST_OVERVIEW_FILTER_TYPES.COUNTRY
          );
        }}
      />
      <SelectionFilter
        title="Region"
        treeData={regions}
        disabled={disabled}
        defaultKeys={customTableFilters.region}
        onFilter={(values: string[]) => {
          updateCustomTableFilterByType(
            values,
            REQUEST_OVERVIEW_FILTER_TYPES.REGION
          );
        }}
      />
      <SelectionFilter
        title="Technology"
        treeData={mapToTreeData(Object.values(DUALIS_TECHNOLOGY))}
        disabled={disabled}
        defaultKeys={customTableFilters.technologies}
        onFilter={(values: string[]) => {
          updateCustomTableFilterByType(
            values,
            REQUEST_OVERVIEW_FILTER_TYPES.TECHNOLOGIES
          );
        }}
      />

      <SelectionFilter
        isDataStatic={true}
        title="Status"
        treeData={mapToTreeData(
          [
            REQUEST_STATUS.REQUESTED,
            REQUEST_STATUS.IN_PROGRESS,
            REQUEST_STATUS.PROPOSED,
            REQUEST_STATUS.IN_RECRUITMENT,
            REQUEST_STATUS.DECLINED,
            REQUEST_STATUS.CONFIRMED,
          ],
          null,
          true
        )}
        disabled={disabled}
        defaultKeys={customTableFilters.status as string[]}
        customRenderer={(node: TreeNode) => (
          <Status status={node.title.toString()} />
        )}
        onFilter={(values: string[]) => {
          updateCustomTableFilterByType(
            values,
            REQUEST_OVERVIEW_FILTER_TYPES.STATUS
          );
        }}
      />
    </>
  );
};

export default FilterStaffingRequests;
